import { fragment, Fragment, ObjectConfig, ObjectState, required } from "@homebound/form-state";
import {
  ReadyPlanOptionGroupPersonalizationType,
  SavePersonalizationOptionGroupInput,
  SavePersonalizationPackageOptionInput,
  SavePersonalizationProductOptionInput,
} from "src/generated/graphqlTypes";
import {
  EditPersonalizationOptionWizard_MaterialVariantFragment,
  EditPersonalizationOptionWizard_OptionCombinationFragment,
  EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment,
  EditPersonalizationOptionWizard_Slot_TakeoffLineItemFragment,
  EditPersonalizationOptionWizardQuery,
} from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import { assertNever, fail, isDefined } from "src/utils";

/** A material variant reference as carried on a product cell */
export type ProductMaterial = { id: string; data: EditPersonalizationOptionWizard_MaterialVariantFragment };

export type PackageOption = Pick<
  SavePersonalizationPackageOptionInput,
  "id" | "name" | "personalizationTargetCombinations" | "curationTargetCombinations" | "checkoutOrder"
> & {
  products: Array<{ placeholderMaterialId: string; locationId?: string; productMaterial: ProductMaterial | undefined }>;
};

/**
 * Deliberately excludes `name` (unlike `PackageOption`) so reads via `packageOptionsProductsSnapshot`
 * don't reactively depend on it — used by filter/column-building logic that runs on every keystroke.
 */
export type PackageOptionProductsSnapshot = Pick<
  PackageOption,
  "id" | "products" | "personalizationTargetCombinations"
>;

export type ProductOption = Pick<
  SavePersonalizationProductOptionInput,
  "materialVariantId" | "personalizationTargetCombinations" | "curationTargetCombinations" | "checkoutOrder"
> & { data: Fragment<EditPersonalizationOptionWizard_MaterialVariantFragment> };

export type FormValue = Omit<
  SavePersonalizationOptionGroupInput,
  "packageOptions" | "productOptions" | "controlledSlots" | "groupByFeature"
> & {
  placeholders: Array<{
    id: string;
    data: Fragment<EditPersonalizationOptionWizard_Slot_TakeoffLineItemFragment>;
    order?: number | null;
  }>;
  productOptions: Array<ProductOption>;
  packageOptions: Array<PackageOption>;
};

/** A single controlled-slot placeholder, rendered as a column in the package variants grid. */
export type PersonalizationPlaceholderRow = ObjectState<FormValue["placeholders"][number]>;

/** A single package-option row in the package variants grid. */
export type PackageOptionRow = ObjectState<PackageOption>;

export function packageOptionsProductsSnapshot(formState: ObjectState<FormValue>): PackageOptionProductsSnapshot[] {
  return formState.packageOptions.rows.map((po) => ({
    id: po.id.value,
    products: po.products.value,
    personalizationTargetCombinations: po.personalizationTargetCombinations.value,
  }));
}

/** A resolved grid column: one per collapsed material, or one per location when expanded. */
export type PlaceholderColumn = {
  /** `mvId` when collapsed, `mvId-locationId` when expanded. */
  key: string;
  materialVariantId: string;
  /** The slot the column draws its header from. */
  placeholder: PersonalizationPlaceholderRow;
  /** Set when expanded; undefined when collapsed. */
  locationId: string | undefined;
  expanded: boolean;
  /** Display location paths for slots represented by this column (one per location when expanded, all when collapsed). */
  locationPaths: string[];
};

/** Opens the duplicate-variant modal for a product cell; shared between the grid column and cells. */
export type OnDuplicateProduct = (
  packageOption: PackageOptionRow,
  column: PlaceholderColumn,
  productMaterial: ProductMaterial,
) => void;

export const formConfig: ObjectConfig<FormValue> = {
  id: { type: "value" },
  type: { type: "value", rules: [required] },
  name: { type: "value", rules: [required] },
  marketingDescription: { type: "value" },
  levels: { type: "value" },
  displayPackageOptionsAsSingleProduct: { type: "value" },
  enableCuration: { type: "value" },
  // Ideally this could be a `type: "fragment"` that was a `TliFragment[]` but
  // currently fragmentField too naively snapshots its value during the 1st init.map
  // run (when we have no data/are loading) and never updates it to the new value.
  placeholders: {
    type: "list",
    config: { id: { type: "value" }, data: { type: "fragment" }, order: { type: "value" } },
  },
  personalizationTargetGroupIds: { type: "value" },
  productOptions: {
    type: "list",
    config: {
      materialVariantId: { type: "value" },
      personalizationTargetCombinations: { type: "list", config: { options: { type: "value" } } },
      curationTargetCombinations: { type: "list", config: { options: { type: "value" } } },
      checkoutOrder: { type: "value" },
      data: { type: "fragment" },
    },
  },
  packageOptions: {
    type: "list",
    config: {
      id: { type: "value" },
      name: { type: "value", rules: [required] },
      products: {
        type: "list",
        config: {
          placeholderMaterialId: { type: "value" },
          locationId: { type: "value" },
          productMaterial: { type: "value" },
        },
      },
      personalizationTargetCombinations: { type: "list", config: { options: { type: "value" } } },
      curationTargetCombinations: { type: "list", config: { options: { type: "value" } } },
      checkoutOrder: { type: "value" },
    },
  },
};

export function mapToForm(data: EditPersonalizationOptionWizardQuery, isAdd: boolean, forTli?: string) {
  if (isAdd) {
    return {
      type: ReadyPlanOptionGroupPersonalizationType.SingleProduct,
      name: "",
      marketingDescription: "",
      levels: undefined,
      // When adding, the caller should pass in the initial placeholder via `forTli`, which we seed
      placeholders: [{ id: forTli ?? fail(), data: fragment(data.takeoffLineItem ?? fail()) }],
      // Product/Package options will be added by `setMaterialVariants` after the user selects the type
      productOptions: [],
      packageOptions: [],
      personalizationTargetGroupIds: data.takeoffLineItem?.targetGroups.map((tg) => tg.id),
    };
  }
  const rpog = data.readyPlan?.optionGroups.first ?? fail("No ReadyPlanOptionGroup found");
  const {
    id,
    name,
    marketingDescription,
    personalizationType,
    personalizationPlaceholders,
    personalizationTargetGroups,
    options,
    levels,
    displayPackageOptionsAsSingleProduct,
    groupByFeature,
    enableCuration,
  } = rpog;
  const type = personalizationType?.code;
  return {
    id,
    type,
    name,
    marketingDescription,
    personalizationTargetGroupIds: personalizationTargetGroups.map((tg) => tg.id),
    levels,
    displayPackageOptionsAsSingleProduct,
    enableCuration,
    // When editing we show our controlled slots
    placeholders:
      personalizationPlaceholders
        ?.flatMap((pp) => pp.personalizationPlaceholderControlledSlots)
        .sortByKey("name")
        .map((tli) => {
          return { id: tli.id, data: fragment(tli), order: tli.personalizationSlotOrder };
        }) ?? [],
    // Form Package types we need to initialize the package options
    ...(type === ReadyPlanOptionGroupPersonalizationType.Package
      ? {
          productOptions: [],
          packageOptions: normalizeLoadedPackageOptions(
            options.map((o) => ({
              id: o.id,
              name: o.name,
              // We scan look at our controlled slots, and their RPO placeholder's product cards, to find
              // find our products, i.e. product cards with "options=[Samsung Appliances]"
              products: personalizationPlaceholders.flatMap(
                (pp) =>
                  pp.personalizationRpoShadowPlaceholder?.products
                    .filter((p) => p.options.every((opt) => opt.id === o.id))
                    .compact()
                    .map((p) => ({
                      placeholderMaterialId: p.placeholder!.materialVariant!.id,
                      productMaterial: p.materialVariant
                        ? { id: p.materialVariant.id, data: p.materialVariant }
                        : undefined,
                      locationId: groupByFeature ? p.location.id : undefined,
                    })) ?? [],
              ),
              personalizationTargetCombinations: rpoCombosToRpoIds(o.personalizationTargetCombinations),
              curationTargetCombinations: rpoCombosToRpoIds(o.curationTargetCombinations),
              checkoutOrder: o.checkoutOrder ?? null,
            })),
            groupByFeature,
          ),
        }
      : // Form Single/Linked types we need to initialize the materials
        {
          productOptions:
            options.map((rpo) => {
              return {
                materialVariantId: rpo.materialVariant!.id,
                data: fragment(rpo.materialVariant!),
                personalizationTargetCombinations: rpoCombosToRpoIds(rpo.personalizationTargetCombinations),
                curationTargetCombinations: rpoCombosToRpoIds(rpo.curationTargetCombinations),
                checkoutOrder: rpo.checkoutOrder ?? null,
              };
            }) ?? [],
          packageOptions: [],
        }),
  };
}

function rpoCombosToRpoIds(combos: { options: { id: string }[] }[]): { options: string[] }[] {
  return combos.map((combo) => ({ options: combo.options.map((o) => o.id) }));
}

/** Removes duplicate combinations from other formState rows based on the current row's selected values */
export function removeDuplicateCombinations<T extends ObjectState<ProductOption> | ObjectState<PackageOption>>(
  currentRow: T,
  allRows: readonly T[],
  selectedValues: string[],
) {
  allRows.forEach((row) => {
    if (row === currentRow) return;
    const current = row.personalizationTargetCombinations.value;
    const filtered = current.filter((c) => !selectedValues.includes(c.options.join(",")));
    if (filtered.length !== current.length) {
      row.personalizationTargetCombinations.set(filtered);
    }
  });
}

/**
 * Builds a map from RPO ID to its parent RPOG ID, to be used for partial combination matching. */
export function buildRpoIdToRpogIdMap(
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[],
): Map<string, string> {
  return new Map(targetGroups.flatMap((rpog) => rpog.options.map((rpo) => [rpo.id, rpog.id] as const)));
}

/**
 * Checks if any of the product's combinations matches all the package option's combinations,
 * supporting partial combinations where omitted option groups = applies to all values.
 *
 * @param productCombinations - Product's target combinations (the pool to search within)
 * @param packageOptionCombinations - Package option's combinations (all must be matched)
 * @param rpoIdToRpogId - Map from RPO ID to its RPOG ID
 * @returns true if all package option combinations have a matching product combination
 */
function hasMatchingPartialCombination(
  productCombinations: { options: string[] }[],
  packageOptionCombinations: { options: string[] }[],
  rpoIdToRpogId: Map<string, string>,
): boolean {
  // All package option combinations must be matched by at least one product combination
  return (
    packageOptionCombinations.nonEmpty &&
    packageOptionCombinations.every((packageOptionCombo) => {
      const pkgRpogIds = packageOptionCombo.options.map((rpoId) => rpoIdToRpogId.get(rpoId)!).unique();
      return productCombinations.some((productCombo) => {
        // If product combo is more specific than package combo, don't match
        // e.g. package [Premium] should not match product [Bold, Premium]
        if (productCombo.options.length > packageOptionCombo.options.length) return false;
        return pkgRpogIds.every((rpogId) => {
          // We're at the RPOG level (e.g. SpecLevel), see what RPO (essential/deluxe/premium) each package/product combo is using
          const pkgRpoForRpog = packageOptionCombo.options.find((rpoId) => rpoIdToRpogId.get(rpoId) === rpogId);
          const prodRpoForRpog = productCombo.options.find((rpoId) => rpoIdToRpogId.get(rpoId) === rpogId);
          // If product combo doesn't have an RPO for this RPOG, we can assume it applies to all, so it's a match
          // Otherwise, check if the RPOs from the product and package combos match
          return !prodRpoForRpog || prodRpoForRpog === pkgRpoForRpog;
        });
      });
    })
  );
}

/** Initialize material variants for a new personalization option */
export function initializeOptions(
  personalizationType: ReadyPlanOptionGroupPersonalizationType,
  formState: ObjectState<FormValue>,
  tli: EditPersonalizationOptionWizard_Slot_TakeoffLineItemFragment,
) {
  // Group products by material variant to handle duplicated cards
  const groupedProducts = tli.products
    .filter((p) => !!p.materialVariant && p.active)
    .groupBy((product) => product.materialVariant!.id);
  // Update product/package options based on the personalization type
  if (
    personalizationType === ReadyPlanOptionGroupPersonalizationType.SingleProduct ||
    personalizationType === ReadyPlanOptionGroupPersonalizationType.LinkedProduct
  ) {
    // We've already initialized, and the useEffect ran again
    if (formState.productOptions.value?.length > 0) return;
    // productOptions only need `materialVariantId` & `combos` set...
    formState.productOptions.set(
      groupedProducts.toEntries().map(([materialVariantId, products]) => ({
        materialVariantId,
        data: fragment(products.first!.materialVariant!),
        // Use all target combinations for the material variant
        personalizationTargetCombinations: rpoCombosToRpoIds(products.flatMap((p) => p.targetCombinations)),
        curationTargetCombinations: [],
      })),
    );
  } else if (personalizationType === ReadyPlanOptionGroupPersonalizationType.Package) {
    // We've already initialized, and the useEffect ran again
    if (formState.packageOptions.value?.length > 0) return;
    // packageOptions need the "column" `placeholderMaterialId` + cells of `productMaterial`s
    // Because this is the 1st placeholder, we create 1 row for each existing card (i.e. two refrigerators become
    // a GE Appliance row & Samsung Appliance row, although the user must still add the option names)
    formState.packageOptions.set(
      groupedProducts.toEntries().map(([_, products]) => ({
        products: [
          {
            placeholderMaterialId: tli.materialVariant!.id,
            productMaterial: { id: products.first!.materialVariant!.id, data: products.first!.materialVariant! },
            // This is a good default for groupByFeature; but if groupByFeature is disabled, it ends up being fine anyway
            // b/c a) the FE code will flip to "unique by placeholderMaterialId", and b) the BE will ignore our locationId
            locationId: tli.location.id,
          },
        ],
        personalizationTargetCombinations: rpoCombosToRpoIds(products.flatMap((p) => p.targetCombinations)),
        curationTargetCombinations: [],
      })),
    );
  } else {
    assertNever(personalizationType);
  }
}

/** Initialize material variants when adding a new slot to a personalization option */
export function addExistingMaterials(
  formState: ObjectState<FormValue>,
  placeholder: EditPersonalizationOptionWizard_Slot_TakeoffLineItemFragment,
  rpoIdToRpogId: Map<string, string>,
) {
  const personalizationType = formState.type.value!;
  if (
    personalizationType === ReadyPlanOptionGroupPersonalizationType.SingleProduct ||
    personalizationType === ReadyPlanOptionGroupPersonalizationType.LinkedProduct
  ) {
    // For Single/Linked options, we should keep new product cards and add them to the material variant pool
    const existingIds = new Set(formState.productOptions.value.map((po) => po.materialVariantId));
    placeholder.products
      .filter((tli) => !!tli.materialVariant && tli.active && !existingIds.has(tli.materialVariant!.id))
      .forEach((tli) => {
        formState.productOptions.add({
          materialVariantId: tli.materialVariant!.id,
          data: fragment(tli.materialVariant!),
          personalizationTargetCombinations: [],
          curationTargetCombinations: [],
          checkoutOrder: nextSequenceNumber(formState.productOptions.value.map((o) => o.checkoutOrder)),
        });
      });
  } else if (personalizationType === ReadyPlanOptionGroupPersonalizationType.Package) {
    // Guard: the add-to-option effect can fire with an empty placeholder before data is ready.
    if (!placeholder.materialVariant) return;
    const placeholderMaterialId = placeholder.materialVariant.id;
    const newLocationId = placeholder.location.id;

    // Which product the new slot contributes to each matching package option (first card wins).
    const incomingByPackageOption = new Map<
      ObjectState<PackageOption>,
      { id: string; data: EditPersonalizationOptionWizard_MaterialVariantFragment }
    >();
    placeholder.products
      .filter((p) => !!p.materialVariant && p.active)
      .groupBy((product) => product.materialVariant!.id)
      .toEntries()
      .forEach(([materialVariantId, products]) => {
        const targetCombinations = rpoCombosToRpoIds(products.flatMap((p) => p.targetCombinations));
        formState.packageOptions.rows
          .filter((po) =>
            hasMatchingPartialCombination(
              targetCombinations,
              po.personalizationTargetCombinations.value,
              rpoIdToRpogId,
            ),
          )
          .forEach((po) => {
            if (!incomingByPackageOption.has(po)) {
              incomingByPackageOption.set(po, { id: materialVariantId, data: products.first!.materialVariant! });
            }
          });
      });

    // A collapsed material whose incoming product differs must expand, not overwrite an existing selection.
    const forcesExpand = [...incomingByPackageOption.entries()].some(([po, incoming]) => {
      const cells = po.products.value.filter((p) => p.placeholderMaterialId === placeholderMaterialId);
      const expandedHere = cells.some((c) => c.locationId != null);
      if (expandedHere || cells.isEmpty) return false;
      return (cells.first!.productMaterial?.id ?? null) !== incoming.id;
    });
    if (forcesExpand && !isMaterialExpanded(formState.packageOptions.value, placeholderMaterialId)) {
      expandMaterial(formState, placeholderMaterialId, [
        ...materialLocationIds(formState.placeholders.rows, placeholderMaterialId),
        newLocationId,
      ]);
    }

    // Place the incoming product: expanded → the new location's cell; collapsed → only if the material
    // isn't present yet (a matching add leaves the shared cell untouched).
    const expanded = isMaterialExpanded(formState.packageOptions.value, placeholderMaterialId);
    incomingByPackageOption.forEach((incoming, po) => {
      const cells = po.products.rows.filter((p) => p.placeholderMaterialId.value === placeholderMaterialId);
      if (expanded) {
        const cell = cells.find((c) => c.locationId.value === newLocationId);
        if (cell) cell.productMaterial.set(incoming);
        else po.products.add({ placeholderMaterialId, productMaterial: incoming, locationId: newLocationId });
      } else if (cells.isEmpty) {
        po.products.add({ placeholderMaterialId, productMaterial: incoming, locationId: undefined });
      }
    });
  } else {
    assertNever(personalizationType);
  }
}

export function maybeFilterByPlaceholder(form: ObjectState<FormValue>) {
  const type = form.type.value;
  if (!type) return;
  if (
    type === ReadyPlanOptionGroupPersonalizationType.LinkedProduct ||
    type === ReadyPlanOptionGroupPersonalizationType.SingleProduct
  ) {
    return form.placeholders.rows.first?.data.value.materialVariant?.id;
  } else if (type === ReadyPlanOptionGroupPersonalizationType.Package) {
    return undefined;
  } else {
    assertNever(type);
  }
}

/** A material attribute dimension (e.g. Finish) and its distinct values (`textValue`s), merged by name across items. */
export type MaterialDimensionOption = { name: string; values: string[] };

/**
 * Distinct brands + material attribute dimensions present across the given material variants, shaped for the
 * variants-grid filters. Dimensions are configured per item, so the same dimension name can appear on several
 * items — they're merged by name, with values deduped by `textValue` (each item's value has its own id).
 */
export function materialFilterOptions(materials: EditPersonalizationOptionWizard_MaterialVariantFragment[]) {
  return {
    brands: materials
      .map((m) => m.listing.brand)
      .compact()
      .uniqueBy((b) => b.id)
      .sortBy((b) => b.name),
    materialDimensions: materials
      .flatMap((m) => m.materialAttributeValues)
      .groupBy((av) => av.dimension.name)
      .toEntries()
      .map(([name, values]): MaterialDimensionOption => ({
        name,
        values: values
          .map((v) => v.textValue)
          .unique()
          .sortBy((t) => t),
      }))
      .sortBy((d) => d.name),
  };
}

/** The distinct placeholder-material ids present across all package options' products. */
function materialIdsOf(packageOptions: PackageOption[]): string[] {
  return packageOptions.flatMap((po) => po.products.map((p) => p.placeholderMaterialId)).unique();
}

/** Whether a material renders expanded — derived from cell shape (its cells carry a `locationId`). */
export function isMaterialExpanded(
  packageOptions: PackageOptionProductsSnapshot[],
  materialVariantId: string,
): boolean {
  return packageOptions.some((po) =>
    po.products.some((p) => p.placeholderMaterialId === materialVariantId && p.locationId != null),
  );
}

/** Whether a material's products actually differ across its locations (value-based) — decides the saved flag. */
export function materialDiffersAcrossLocations(
  packageOptions: PackageOptionProductsSnapshot[],
  materialVariantId: string,
): boolean {
  return packageOptions.some(
    (po) =>
      po.products
        .filter((p) => p.placeholderMaterialId === materialVariantId)
        .map((p) => p.productMaterial?.id ?? null)
        .unique().length > 1,
  );
}

/** Whether collapsing to `winningLocationId` would drop a differing product (→ whether to confirm first). */
export function collapseWouldDiscardProducts(
  packageOptions: PackageOptionProductsSnapshot[],
  materialVariantId: string,
  winningLocationId: string,
): boolean {
  return packageOptions.some((po) => {
    const cells = po.products.filter((p) => p.placeholderMaterialId === materialVariantId);
    const winnerId = cells.find((p) => p.locationId === winningLocationId)?.productMaterial?.id ?? null;
    return cells.some((p) => p.locationId !== winningLocationId && (p.productMaterial?.id ?? null) !== winnerId);
  });
}

/** The distinct locations a material is controlled at, in slot order. */
export function materialLocationIds(
  placeholders: readonly PersonalizationPlaceholderRow[],
  materialVariantId: string,
): string[] {
  return placeholders
    .sortBy((p) => p.order.value ?? Infinity)
    .filter((p) => p.data.value.materialVariant?.id === materialVariantId)
    .map((p) => p.data.value.location.id)
    .unique();
}

/** Grid columns per material: one when collapsed, one per location when expanded; grouped so a material's columns stay contiguous. */
export function buildPlaceholderColumns(
  orderedSlots: PersonalizationPlaceholderRow[],
  packageOptions: PackageOptionProductsSnapshot[],
): PlaceholderColumn[] {
  return [...orderedSlots]
    .groupByObject((slot) => slot.data.value.materialVariant?.id)
    .flatMap(([materialVariantId, slots]): PlaceholderColumn[] => {
      if (!materialVariantId) return [];
      if (isMaterialExpanded(packageOptions, materialVariantId)) {
        return slots.map((slot) => ({
          key: `${materialVariantId}-${slot.data.value.location.id}`,
          materialVariantId,
          placeholder: slot,
          locationId: slot.data.value.location.id,
          expanded: true,
          locationPaths: [slot.data.value.location.displayLocationPath],
        }));
      }
      return [
        {
          key: materialVariantId,
          materialVariantId,
          placeholder: slots.first!,
          locationId: undefined,
          expanded: false,
          locationPaths: slots.map((s) => s.data.value.location.displayLocationPath),
        },
      ];
    });
}

/** True once this slot has been decided — either an assigned product or an explicit "No Product". */
export function isColumnFilled(packageOption: PackageOptionProductsSnapshot, column: PlaceholderColumn): boolean {
  return packageOption.products.some((p) => {
    const sameMv = p.placeholderMaterialId === column.materialVariantId;
    const sameLocation = column.expanded ? p.locationId === column.locationId : p.locationId == null;
    return sameMv && sameLocation;
  });
}

/**
 * A combo counts as "missing products" if none of the package options claiming it have filled
 * every slot (an explicit "No Product" counts as filled). That covers two real cases with one check:
 *   1. Nobody claimed the combo at all -> zero claimants -> vacuously "none of them are complete".
 *   2. Someone claimed it, but left a slot undecided.
 */
export function getCombinationsMissingProducts(
  packageOptions: PackageOptionProductsSnapshot[],
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[],
  columns: PlaceholderColumn[],
): EditPersonalizationOptionWizard_OptionCombinationFragment[] {
  return targetCombinations.filter((combo) => {
    const key = combo.options.map((o) => o.id).join(",");
    const claimants = packageOptions.filter((po) =>
      po.personalizationTargetCombinations?.some((pc) => pc.options.join(",") === key),
    );
    return claimants.every((po) => columns.some((c) => !isColumnFilled(po, c)));
  });
}

/** Splits a collapsed material into one cell per location, copying its shared product into each. */
export function expandMaterial(formState: ObjectState<FormValue>, materialVariantId: string, locationIds: string[]) {
  const locations = locationIds.unique();
  formState.packageOptions.rows.forEach((po) => {
    const cells = po.products.rows.filter((p) => p.placeholderMaterialId.value === materialVariantId);
    if (cells.isEmpty) return;
    const shared = cells.first!.productMaterial.value;
    cells.forEach((c) => po.products.remove(c.value));
    locations.forEach((locationId) =>
      po.products.add({ placeholderMaterialId: materialVariantId, locationId, productMaterial: shared }),
    );
  });
}

/** Collapses a material to one cell, keeping `winningLocationId`'s product and dropping the rest. */
export function collapseMaterialToLocation(
  formState: ObjectState<FormValue>,
  materialVariantId: string,
  winningLocationId: string,
) {
  formState.packageOptions.rows.forEach((po) => {
    const cells = po.products.rows.filter((p) => p.placeholderMaterialId.value === materialVariantId);
    if (cells.isEmpty) return;
    const winningProduct = cells.find((c) => c.locationId.value === winningLocationId)?.productMaterial.value;
    cells.forEach((c) => po.products.remove(c.value));
    po.products.add({
      placeholderMaterialId: materialVariantId,
      locationId: undefined,
      productMaterial: winningProduct,
    });
  });
}

/** A single duplication target: one package option's cell for the material being duplicated. */
export type DuplicateTargetLeaf = {
  packageOption: ObjectState<PackageOption>;
  /** Undefined for a collapsed/grouped material (its single shared cell); a location id when expanded. */
  locationId: string | undefined;
  isEmpty: boolean;
};

/** One package option's duplication targets, grouped for tree-select display. */
export type DuplicateTargetGroup = {
  packageOption: ObjectState<PackageOption>;
  packageOptionName: string;
  leaves: DuplicateTargetLeaf[];
};

function isDuplicateTargetEmpty(
  products: PackageOption["products"],
  materialVariantId: string,
  locationId: string | undefined,
): boolean {
  return !products.find(
    (p) => p.placeholderMaterialId === materialVariantId && (p.locationId ?? undefined) === locationId,
  )?.productMaterial;
}

/**
 * Duplication targets for `materialVariantId`: one group per package option, with one leaf per
 * location when the material is expanded, or a single (locationless) leaf when collapsed — duplicating
 * into a grouped feature fills its one shared cell, which fans across all its locations on save.
 */
export function buildDuplicateTargets(
  formState: ObjectState<FormValue>,
  materialVariantId: string,
): DuplicateTargetGroup[] {
  const expanded = isMaterialExpanded(formState.packageOptions.value, materialVariantId);
  const locationIds: (string | undefined)[] = expanded
    ? materialLocationIds(formState.placeholders.rows, materialVariantId)
    : [undefined];
  return formState.packageOptions.rows.map((po) => ({
    packageOption: po,
    packageOptionName: po.name.value ?? "",
    leaves: locationIds.map((locationId) => ({
      packageOption: po,
      locationId,
      isEmpty: isDuplicateTargetEmpty(po.products.value, materialVariantId, locationId),
    })),
  }));
}

/**
 * Whether `materialVariantId` has anywhere valid to duplicate its product to — called once per grid
 * cell, so (unlike `buildDuplicateTargets`, only called when the duplicate modal opens) this avoids `name`.
 */
export function hasEmptyDuplicateTarget(formState: ObjectState<FormValue>, materialVariantId: string): boolean {
  const packageOptions = packageOptionsProductsSnapshot(formState);
  const expanded = isMaterialExpanded(packageOptions, materialVariantId);
  const locationIds: (string | undefined)[] = expanded
    ? materialLocationIds(formState.placeholders.rows, materialVariantId)
    : [undefined];
  return packageOptions.some((po) =>
    locationIds.some((locationId) => isDuplicateTargetEmpty(po.products, materialVariantId, locationId)),
  );
}

/** Copies `productMaterial` into each target cell for `materialVariantId` (targets are assumed empty). */
export function duplicateProductToTargets(
  materialVariantId: string,
  productMaterial: ProductMaterial | undefined,
  targets: DuplicateTargetLeaf[],
) {
  targets.forEach(({ packageOption, locationId }) => {
    const cell = packageOption.products.rows.find(
      (p) => p.placeholderMaterialId.value === materialVariantId && (p.locationId.value ?? undefined) === locationId,
    );
    if (cell) {
      cell.productMaterial.set(productMaterial);
    } else {
      packageOption.products.add({ placeholderMaterialId: materialVariantId, locationId, productMaterial });
    }
  });
}

/** On load, collapse materials that are uniform across their locations (no-op for non-grouped groups). */
export function normalizeLoadedPackageOptions(
  packageOptions: PackageOption[],
  wasGroupedByFeature: boolean,
): PackageOption[] {
  if (!wasGroupedByFeature) return packageOptions;
  const materialIds = materialIdsOf(packageOptions);
  const uniformMaterialIds = new Set(materialIds.filter((mv) => !materialDiffersAcrossLocations(packageOptions, mv)));
  return packageOptions.map((po) => ({
    ...po,
    products: po.products
      .groupByObject((p) => p.placeholderMaterialId)
      .flatMap(([mv, cells]) => (uniformMaterialIds.has(mv) ? [{ ...cells.first!, locationId: undefined }] : cells)),
  }));
}

/**
 * Derives the saved (group-level) `groupByFeature` flag + normalized products from the mixed form state:
 * nothing differs → `false`, one product per material; any differs → `true`, one per (material, location)
 * with collapsed materials fanned across their locations.
 */
export function getGroupByFeatureForSave(
  packageOptions: PackageOption[],
  locationIdsByMaterial: Record<string, string[]>,
): { groupByFeature: boolean; packageOptions: PackageOption[] } {
  const materialIds = materialIdsOf(packageOptions);
  const anyDiffers = materialIds.some((mv) => materialDiffersAcrossLocations(packageOptions, mv));
  if (!anyDiffers) {
    return {
      groupByFeature: false,
      packageOptions: packageOptions.map((po) => ({
        ...po,
        products: po.products
          .groupByObject((p) => p.placeholderMaterialId)
          .map(([, cells]) => ({ ...cells.first!, locationId: undefined })),
      })),
    };
  }
  return {
    groupByFeature: true,
    packageOptions: packageOptions.map((po) => ({
      ...po,
      products: po.products
        .groupByObject((p) => p.placeholderMaterialId)
        .flatMap(([mv, cells]) => {
          // Already per-location → keep as-is; collapsed → fan its single product across its locations.
          if (cells.some((c) => c.locationId != null)) return cells;
          const shared = cells.first!;
          return (locationIdsByMaterial[mv] ?? []).map((locationId) => ({ ...shared, locationId }));
        }),
    })),
  };
}

export function nextSequenceNumber(values: (number | null | undefined)[]): number | undefined {
  const max = values.compact().max();
  return max === undefined ? undefined : max + 1;
}

/**
 * Moves the edited row to its typed position (clamped to 1..n) and renumbers the participating rows to a contiguous 1..n.
 *
 * `fillBlanks` controls how rows without an order are treated:
 * - `false`: blank rows stay blank and only already-ordered rows participate; the clamp ceiling is the count of ordered rows.
 * - `true`: as soon as any row has an order, every row participates (blanks sort to the end) so the whole
 *   set stays contiguous; the clamp ceiling is the total row count. While every row is blank, it's a no-op.
 */
export function reorderRows<Id extends string | number>(
  rows: Array<{ id: Id; order: number | null | undefined }>,
  editedId: Id,
  options: { fillBlanks?: boolean } = {},
): Array<{ id: Id; order: number | null }> {
  const { fillBlanks = false } = options;
  const [rowsWithOrder, rowsWithoutOrder] = rows.partition((r) => isDefined(r.order));
  // The rows that get renumbered vs. those left blank.
  const shouldFillBlanks = fillBlanks && rowsWithOrder.nonEmpty;
  const participating = shouldFillBlanks ? rows : rowsWithOrder;
  const blanks = shouldFillBlanks ? [] : rowsWithoutOrder;
  // Drop the edited row, sort the rest by order (blanks last), then reinsert at the clamped position.
  const editedRow = participating.find((r) => r.id === editedId);
  const ordered = participating.filter((r) => r.id !== editedId).sortBy((r) => r.order ?? Infinity);
  if (editedRow) {
    const targetPos =
      editedRow.order == null ? participating.length : Math.min(Math.max(1, editedRow.order), participating.length);
    ordered.splice(targetPos - 1, 0, editedRow);
  }
  return [...ordered.map((r, i) => ({ id: r.id, order: i + 1 })), ...blanks.map((r) => ({ id: r.id, order: null }))];
}
